<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import AiKnowledgeAPI, { aiExamplesAPI } from 'dashboard/api/aiKnowledge';

const { t } = useI18n();

const TABS = ['knowledge', 'examples'];
const KINDS = ['price', 'policy', 'canned', 'template'];

const activeTab = ref('knowledge');
const isLoading = ref(false);
const entries = ref([]);
const examples = ref([]);
const meta = ref({});
const exampleMeta = ref({});
const statusFilter = ref('pending');
const kindFilter = ref('');
const selected = ref([]);
const mining = ref({ state: 'idle' });
const editing = ref(null);
const pollTimer = ref(null);

const isMining = computed(() =>
  ['queued', 'running'].includes(mining.value?.state)
);

const fetchEntries = async () => {
  isLoading.value = true;
  try {
    const params = {};
    if (statusFilter.value) params.status = statusFilter.value;
    if (kindFilter.value) params.kind = kindFilter.value;
    const { data } = await AiKnowledgeAPI.list(params);
    entries.value = data.payload || [];
    meta.value = data.meta || {};
    mining.value = data.meta?.mining || { state: 'idle' };
  } catch (error) {
    useAlert(t('AI_KNOWLEDGE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const fetchExamples = async () => {
  isLoading.value = true;
  try {
    const params = statusFilter.value ? { status: statusFilter.value } : {};
    const { data } = await aiExamplesAPI.list(params);
    examples.value = data.payload || [];
    exampleMeta.value = data.meta || {};
    selected.value = [];
  } catch (error) {
    useAlert(t('AI_KNOWLEDGE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const refresh = () =>
  activeTab.value === 'knowledge' ? fetchEntries() : fetchExamples();

const switchTab = tab => {
  activeTab.value = tab;
  refresh();
};

const setEntryStatus = async (entry, status) => {
  try {
    await AiKnowledgeAPI.patch(entry.id, { status });
    entries.value = entries.value.filter(item => item.id !== entry.id);
  } catch (error) {
    useAlert(t('AI_KNOWLEDGE.SAVE_ERROR'));
  }
};

const setExampleStatus = async (example, status) => {
  try {
    await aiExamplesAPI.patch(example.id, { status });
    examples.value = examples.value.filter(item => item.id !== example.id);
  } catch (error) {
    useAlert(t('AI_KNOWLEDGE.SAVE_ERROR'));
  }
};

const toggleSelection = id => {
  selected.value = selected.value.includes(id)
    ? selected.value.filter(item => item !== id)
    : [...selected.value, id];
};

const bulk = async status => {
  if (!selected.value.length) return;
  try {
    await aiExamplesAPI.bulk(selected.value, status);
    useAlert(t('AI_KNOWLEDGE.BULK_DONE', { count: selected.value.length }));
    fetchExamples();
  } catch (error) {
    useAlert(t('AI_KNOWLEDGE.SAVE_ERROR'));
  }
};

const startEditing = entry => {
  editing.value = entry
    ? { ...entry }
    : { kind: 'price', title: '', content: '', status: 'approved' };
};

const saveEditing = async () => {
  const payload = {
    kind: editing.value.kind,
    title: editing.value.title,
    content: editing.value.content,
    status: editing.value.status,
  };
  try {
    if (editing.value.id) {
      await AiKnowledgeAPI.patch(editing.value.id, payload);
    } else {
      await AiKnowledgeAPI.save(payload);
    }
    editing.value = null;
    fetchEntries();
    useAlert(t('AI_KNOWLEDGE.SAVED'));
  } catch (error) {
    useAlert(t('AI_KNOWLEDGE.SAVE_ERROR'));
  }
};

const startMining = async () => {
  try {
    await AiKnowledgeAPI.mine();
    mining.value = { state: 'queued' };
    useAlert(t('AI_KNOWLEDGE.MINING_STARTED'));
    pollMining();
  } catch (error) {
    useAlert(t('AI_KNOWLEDGE.SAVE_ERROR'));
  }
};

const pollMining = () => {
  clearInterval(pollTimer.value);
  pollTimer.value = setInterval(async () => {
    await fetchEntries();
    if (!isMining.value) clearInterval(pollTimer.value);
  }, 4000);
};

onMounted(fetchEntries);
onUnmounted(() => clearInterval(pollTimer.value));
</script>

<template>
  <div class="flex flex-col gap-4 p-6 max-w-6xl mx-auto w-full">
    <div class="flex items-start justify-between gap-4">
      <div class="flex flex-col gap-1">
        <h1 class="text-xl font-medium text-n-slate-12">
          {{ $t('AI_KNOWLEDGE.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ $t('AI_KNOWLEDGE.DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        blue
        sm
        icon="i-lucide-database"
        :is-loading="isMining"
        :disabled="isMining"
        :label="$t('AI_KNOWLEDGE.MINE')"
        @click="startMining"
      />
    </div>

    <div
      v-if="isMining || mining.state === 'done'"
      class="rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-11"
    >
      <span v-if="isMining">
        {{ $t('AI_KNOWLEDGE.MINING_RUNNING', { step: mining.step || '' }) }}
      </span>
      <span v-else>
        {{
          $t('AI_KNOWLEDGE.MINING_DONE', {
            templates: mining.templates || 0,
            examples: mining.examples || 0,
          })
        }}
      </span>
    </div>

    <div class="flex items-center gap-2 border-b border-n-weak">
      <button
        v-for="tab in TABS"
        :key="tab"
        class="px-3 py-2 text-sm border-b-2 -mb-px transition-colors"
        :class="
          activeTab === tab
            ? 'border-n-brand text-n-slate-12 font-medium'
            : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
        "
        @click="switchTab(tab)"
      >
        {{ $t(`AI_KNOWLEDGE.TABS.${tab.toUpperCase()}`) }}
      </button>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <select
        v-model="statusFilter"
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm text-n-slate-12"
        @change="refresh"
      >
        <option value="pending">{{ $t('AI_KNOWLEDGE.STATUS.PENDING') }}</option>
        <option value="approved">
          {{ $t('AI_KNOWLEDGE.STATUS.APPROVED') }}
        </option>
        <option value="rejected">
          {{ $t('AI_KNOWLEDGE.STATUS.REJECTED') }}
        </option>
        <option value="">{{ $t('AI_KNOWLEDGE.STATUS.ALL') }}</option>
      </select>

      <select
        v-if="activeTab === 'knowledge'"
        v-model="kindFilter"
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm text-n-slate-12"
        @change="fetchEntries"
      >
        <option value="">{{ $t('AI_KNOWLEDGE.KINDS.ALL') }}</option>
        <option v-for="kind in KINDS" :key="kind" :value="kind">
          {{ $t(`AI_KNOWLEDGE.KINDS.${kind.toUpperCase()}`) }}
        </option>
      </select>

      <NextButton
        v-if="activeTab === 'knowledge'"
        slate
        faded
        sm
        icon="i-lucide-plus"
        :label="$t('AI_KNOWLEDGE.NEW')"
        @click="startEditing(null)"
      />

      <template v-if="activeTab === 'examples' && selected.length">
        <NextButton
          teal
          sm
          icon="i-lucide-check"
          :label="$t('AI_KNOWLEDGE.APPROVE_SELECTED', { count: selected.length })"
          @click="bulk('approved')"
        />
        <NextButton
          ruby
          faded
          sm
          icon="i-lucide-x"
          :label="$t('AI_KNOWLEDGE.REJECT_SELECTED')"
          @click="bulk('rejected')"
        />
      </template>
    </div>

    <div v-if="isLoading" class="flex items-center gap-2 text-sm text-n-slate-11">
      <span class="i-lucide-loader-circle size-4 animate-spin" />
      {{ $t('AI_KNOWLEDGE.LOADING') }}
    </div>

    <!-- knowledge -->
    <div v-else-if="activeTab === 'knowledge'" class="flex flex-col gap-2">
      <p v-if="!entries.length" class="text-sm text-n-slate-11 py-8 text-center">
        {{ $t('AI_KNOWLEDGE.EMPTY') }}
      </p>
      <div
        v-for="entry in entries"
        :key="entry.id"
        class="rounded-lg border border-n-weak p-3 flex flex-col gap-2"
      >
        <div class="flex items-center justify-between gap-2">
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="text-xs px-1.5 py-0.5 rounded-full shrink-0"
              :class="
                entry.kind === 'price'
                  ? 'bg-n-amber-3 text-n-amber-11'
                  : 'bg-n-alpha-2 text-n-slate-11'
              "
            >
              {{ $t(`AI_KNOWLEDGE.KINDS.${entry.kind.toUpperCase()}`) }}
            </span>
            <span class="text-xs text-n-slate-10 shrink-0">
              {{ $t('AI_KNOWLEDGE.SOURCE_COUNT', { count: entry.source_count }) }}
            </span>
          </div>
          <div class="flex items-center gap-1 shrink-0">
            <NextButton
              v-tooltip.top="$t('AI_KNOWLEDGE.EDIT')"
              ghost
              slate
              xs
              icon="i-lucide-pencil"
              @click="startEditing(entry)"
            />
            <NextButton
              v-if="entry.status !== 'approved'"
              v-tooltip.top="$t('AI_KNOWLEDGE.APPROVE')"
              ghost
              teal
              xs
              icon="i-lucide-check"
              @click="setEntryStatus(entry, 'approved')"
            />
            <NextButton
              v-if="entry.status !== 'rejected'"
              v-tooltip.top="$t('AI_KNOWLEDGE.REJECT')"
              ghost
              ruby
              xs
              icon="i-lucide-x"
              @click="setEntryStatus(entry, 'rejected')"
            />
          </div>
        </div>
        <p class="text-sm text-n-slate-12 whitespace-pre-wrap break-words">
          {{ entry.content }}
        </p>
      </div>
    </div>

    <!-- examples -->
    <div v-else class="flex flex-col gap-2">
      <p v-if="!examples.length" class="text-sm text-n-slate-11 py-8 text-center">
        {{ $t('AI_KNOWLEDGE.EMPTY') }}
      </p>
      <div
        v-for="example in examples"
        :key="example.id"
        class="rounded-lg border border-n-weak p-3 flex gap-3"
      >
        <input
          type="checkbox"
          class="mt-1 shrink-0"
          :checked="selected.includes(example.id)"
          @change="toggleSelection(example.id)"
        />
        <div class="flex flex-col gap-1 min-w-0 flex-1">
          <p class="text-sm text-n-slate-11">
            <span class="font-medium">{{ $t('AI_KNOWLEDGE.CUSTOMER') }}:</span>
            {{ example.customer_text }}
          </p>
          <p class="text-sm text-n-slate-12">
            <span class="font-medium">{{ $t('AI_KNOWLEDGE.AGENT') }}:</span>
            {{ example.agent_text }}
          </p>
          <span class="text-xs text-n-slate-10">
            {{ example.agent_name }}
            <template v-if="example.labels && example.labels.length">
              · {{ example.labels.join(', ') }}
            </template>
          </span>
        </div>
        <div class="flex items-start gap-1 shrink-0">
          <NextButton
            v-if="example.status !== 'approved'"
            v-tooltip.top="$t('AI_KNOWLEDGE.APPROVE')"
            ghost
            teal
            xs
            icon="i-lucide-check"
            @click="setExampleStatus(example, 'approved')"
          />
          <NextButton
            v-if="example.status !== 'rejected'"
            v-tooltip.top="$t('AI_KNOWLEDGE.REJECT')"
            ghost
            ruby
            xs
            icon="i-lucide-x"
            @click="setExampleStatus(example, 'rejected')"
          />
        </div>
      </div>
    </div>

    <!-- editor -->
    <div
      v-if="editing"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="editing = null"
    >
      <div
        class="bg-n-solid-1 rounded-xl border border-n-weak w-full max-w-2xl p-4 flex flex-col gap-3"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ $t('AI_KNOWLEDGE.EDITOR_TITLE') }}
        </h3>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ $t('AI_KNOWLEDGE.FIELD_KIND') }}
          </span>
          <select
            v-model="editing.kind"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          >
            <option v-for="kind in KINDS" :key="kind" :value="kind">
              {{ $t(`AI_KNOWLEDGE.KINDS.${kind.toUpperCase()}`) }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ $t('AI_KNOWLEDGE.FIELD_TITLE') }}
          </span>
          <input
            v-model="editing.title"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm text-n-slate-11">
            {{ $t('AI_KNOWLEDGE.FIELD_CONTENT') }}
          </span>
          <textarea
            v-model="editing.content"
            rows="10"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </label>
        <div class="flex justify-end gap-2">
          <NextButton
            slate
            faded
            sm
            :label="$t('AI_KNOWLEDGE.CANCEL')"
            @click="editing = null"
          />
          <NextButton
            blue
            sm
            :label="$t('AI_KNOWLEDGE.SAVE')"
            @click="saveEditing"
          />
        </div>
      </div>
    </div>
  </div>
</template>
